import { mods } from "../../utils/mods";
import { banks } from "./banks";

import type { BankValidationObject } from "./banks";

export interface BankInfo {
	bankName: string;
	clearingNumber: string;
	accountNumber: string;
}

banks.sort((a, b) => {
	return a.name < b.name ? -1 : 1;
});

const utils = {
	getClearingNumber: (accountNumber: string) => {
		return accountNumber.substr(0, accountNumber.charAt(0) === "8" ? 5 : 4);
	},
	getType2Comment3AccountNumber: (accountNumber: string) => {
		const clearingNumberLength = utils.getClearingNumber(accountNumber).length;

		return accountNumber.substr(clearingNumberLength);
	},
	getType2AccountNumber: (accountNumber: string, bank: BankValidationObject) => {
		let number;

		if (bank.comment === 2) {
			number = accountNumber.substr(-9);
		} else if (bank.comment === 3) {
			number = utils.getType2Comment3AccountNumber(accountNumber);
		}

		return number || accountNumber.substr(-10);
	},
	getAccountNumber: (accountNumber: string, bank: BankValidationObject) => {
		return bank.type === 1 ? accountNumber.substr(-7) : utils.getType2AccountNumber(accountNumber, bank);
	},
	modResult: {
		type1: {
			comment1: (number: string, clearing: string) => {
				return mods.mod11((clearing + number).substr(-10));
			},
			comment2: (number: string, clearing: string) => {
				return mods.mod11(clearing + number);
			},
		},
		type2: {
			comment2: (number: string) => {
				return mods.mod11(number);
			},
		},
		defaultType: (number: string) => {
			return mods.mod10(number);
		},
		getModFunc: (
			clearing: string,
			number: string,
			bank: BankValidationObject,
		): ((number: string, clearing?: string) => boolean | 0) => {
			// SS 2023-12-22 The types I added here are just to get the things to compile under mobile app's setting, I didn't strive for fixing everything's type properly.
			const typeFunc = utils.modResult[("type" + bank.type) as keyof typeof utils.modResult] as Record<string, unknown>;
			const commentFunc = typeFunc
				? (typeFunc["comment" + bank.comment] as (number: string, clearing?: string) => boolean | 0)
				: undefined;

			return commentFunc || utils.modResult.defaultType;
		},
	},
	getModResult: (clearing: string, number: string, bank: BankValidationObject) => {
		const modFunc = utils.modResult.getModFunc(clearing, number, bank);

		return modFunc(number, clearing);
	},
	justDigits: (str: string): string => {
		return str.replace(/\D/g, "");
	},
	validate: (accountNumber: string, bank: BankValidationObject): BankInfo | null => {
		const clearing = utils.getClearingNumber(accountNumber);
		const number = utils.getAccountNumber(accountNumber, bank);
		const modResult = utils.getModResult(clearing, number, bank);

		return !modResult
			? null
			: {
					bankName: bank.name,
					clearingNumber: clearing,
					accountNumber: number,
				};
	},
};

export class BankAccountValidator {
	static validateClearingNumber(clearingNumber: string | undefined): string | undefined {
		if (!clearingNumber) {
			return;
		}

		const bankObj = banks.find(bank => {
			return bank.clearingRegex && bank.clearingRegex.test(utils.justDigits(clearingNumber));
		});

		return bankObj && bankObj.name;
	}

	static validate(accountNumber: string | undefined): BankInfo | null {
		if (!accountNumber) {
			return null;
		}

		let bankObj: BankInfo | null = null;
		const accountNumberDigits = utils.justDigits(accountNumber);
		banks.forEach(bank => {
			if (!bankObj && bank.regex.test(accountNumberDigits)) {
				bankObj = utils.validate(accountNumberDigits, bank);
			}
		});

		return bankObj;
	}
}
